import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Plus, Trash2, Edit3, Save, X, Search, Shield, User as UserIcon, Mail, Loader2 } from 'lucide-react';

export default function UserManager() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | admins | members
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/users');
      setUsers(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = users.filter(u => {
    if (filter === 'admins' && !u.is_admin) return false;
    if (filter === 'members' && u.is_admin) return false;
    if (q && !((u.email||'') + ' ' + (u.name||'')).toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const startCreate = () => { setEditing({ id: null, name: '', email: '', password: '', is_admin: false }); setErr(''); };
  const startEdit = (u) => { setEditing({ ...u, password: '' }); setErr(''); };
  const close = () => { setEditing(null); setErr(''); };

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      if (editing.id) {
        const body = { name: editing.name, email: editing.email, is_admin: editing.is_admin };
        if (editing.password) body.password = editing.password;
        await api.patch(`/admin/users/${editing.id}`, body);
      } else {
        await api.post('/admin/users', editing);
      }
      close(); load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    if (me?.id === u.id) { window.alert("You can't delete your own account while logged in."); return; }
    if (!window.confirm(`Permanently delete ${u.email}? This also removes their community posts and vision boards.`)) return;
    await api.delete(`/admin/users/${u.id}`);
    load();
  };

  return (
    <div data-testid="user-manager">
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] tracking-[0.18em] uppercase text-muted-kc">Users</div>
          <div className="font-display uppercase text-2xl mt-1">{users.length} total · {users.filter(u => u.is_admin).length} admin</div>
        </div>
        <button onClick={startCreate} className="h-10 px-4 inline-flex items-center gap-1 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)]" data-testid="user-add-btn"><Plus size={14}/> Add user</button>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[{id:'all',l:'All'},{id:'admins',l:'Admins'},{id:'members',l:'Members'}].map(t => (
          <button key={t.id} onClick={() => setFilter(t.id)} className={`px-3 h-8 text-xs font-semibold uppercase tracking-wider rounded-full ${filter === t.id ? 'bg-ink text-white' : 'bg-surface-2 text-ink-2 hover:bg-kc'}`} data-testid={`user-filter-${t.id}`}>{t.l}</button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-kc"/>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email…" className="h-9 pl-9 pr-3 rounded-[var(--kc-radius-sm)] border border-kc-strong text-sm w-64" data-testid="user-search"/>
        </div>
      </div>

      <div className="bg-white border border-kc rounded-[var(--kc-radius-md)] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-2 text-left text-xs uppercase tracking-wider">
            <tr><th className="p-3">User</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Joined</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-6 text-center text-muted-kc">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-muted-kc">No users match.</td></tr>}
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-kc">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full bg-ink text-white inline-flex items-center justify-center font-bold">{(u.name||u.email||'?')[0].toUpperCase()}</div>
                    <div className="font-semibold">{u.name || '—'}</div>
                  </div>
                </td>
                <td className="p-3 text-muted-kc">{u.email}</td>
                <td className="p-3">{u.is_admin ? <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gold-soft text-ink inline-flex items-center gap-1"><Shield size={10}/> Admin</span> : <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-surface-2 text-ink-2 inline-flex items-center gap-1"><UserIcon size={10}/> Member</span>}</td>
                <td className="p-3 text-muted-kc text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ''}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <button onClick={() => startEdit(u)} className="text-xs font-semibold inline-flex items-center gap-1 hover:text-[color:var(--kc-gold-2)] mr-3" data-testid={`user-edit-${u.id}`}><Edit3 size={12}/> Edit</button>
                  <button onClick={() => remove(u)} className="text-xs font-semibold inline-flex items-center gap-1 text-[color:var(--kc-danger)] hover:underline disabled:opacity-30" disabled={me?.id === u.id} data-testid={`user-del-${u.id}`}><Trash2 size={12}/> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-0 md:p-4" data-testid="user-editor-modal">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={close}/>
          <div className="relative w-full md:max-w-lg bg-white rounded-t-2xl md:rounded-[var(--kc-radius-md)] shadow-2xl">
            <div className="px-5 py-3 border-b border-kc flex items-center justify-between">
              <div className="font-display uppercase text-lg">{editing.id ? 'Edit user' : 'Add user'}</div>
              <button onClick={close} className="w-9 h-9 inline-flex items-center justify-center rounded-full hover:bg-surface-2"><X size={16}/></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Name</label>
                <input value={editing.name || ''} onChange={(e) => setEditing({...editing, name: e.target.value})} className="kc-input" data-testid="user-name"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1">Email *</label>
                <input type="email" value={editing.email || ''} onChange={(e) => setEditing({...editing, email: e.target.value})} className="kc-input" data-testid="user-email"/>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1">{editing.id ? 'New password (leave blank to keep current)' : 'Password * (min 8 chars)'}</label>
                <input type="text" value={editing.password || ''} onChange={(e) => setEditing({...editing, password: e.target.value})} className="kc-input font-mono" placeholder="At least 8 characters" data-testid="user-password"/>
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={!!editing.is_admin} onChange={(e) => setEditing({...editing, is_admin: e.target.checked})} data-testid="user-is-admin"/>
                <Shield size={14} className="text-[color:var(--kc-gold-2)]"/> Make admin (full dashboard access)
              </label>
              {err && <div className="p-2 bg-red-50 border border-red-200 text-[color:var(--kc-danger)] rounded-[var(--kc-radius-sm)] text-sm" data-testid="user-err">{err}</div>}
            </div>
            <div className="px-5 py-3 border-t border-kc flex items-center justify-end gap-2">
              <button onClick={close} className="h-10 px-4 rounded-[var(--kc-radius-sm)] text-sm font-semibold hover:bg-surface-2">Cancel</button>
              <button onClick={save} disabled={saving} className="h-10 px-5 rounded-[var(--kc-radius-sm)] bg-[color:var(--kc-gold)] text-black text-sm font-semibold hover:bg-[color:var(--kc-gold-2)] inline-flex items-center gap-1 disabled:opacity-50" data-testid="user-save">{saving ? <><Loader2 size={14} className="animate-spin"/> Saving</> : <><Save size={14}/> Save user</>}</button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        .kc-input { width: 100%; padding: 0.55rem 0.75rem; border: 1px solid var(--kc-border-strong); border-radius: var(--kc-radius-sm); font-size: 0.9rem; outline: none; background: white; }
        .kc-input:focus { border-color: var(--kc-gold); box-shadow: 0 0 0 2px var(--kc-gold-soft); }
      `}</style>
    </div>
  );
}
